/**
 * Slim Axios client. One request interceptor (attach bearer) and one response
 * interceptor (401 → refresh once → replay). No analytics, no offline queue,
 * no circuit breaker — all of those are premature for the MVP and can be added
 * later if a real signal emerges.
 *
 * The auth store imports this module and registers token getters/setters,
 * keeping auth state out of the client itself.
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import { env } from './env'

type TokenProvider = () => string | null
type TokenSetter = (accessToken: string, refreshToken: string) => void
type LogoutHandler = () => void
type RefreshExecutor = (refreshToken: string) => Promise<{
  accessToken: string
  refreshToken: string
}>

let getAccessToken: TokenProvider = () => null
let getRefreshToken: TokenProvider = () => null
let setTokens: TokenSetter = () => {}
let onLogout: LogoutHandler = () => {}
let executeRefresh: RefreshExecutor | null = null
// Pull the latest persisted tokens into memory before refreshing — used to pick
// up a rotation performed by another tab. No-op until the store wires it.
let syncTokensFromStorage: () => void | Promise<void> = () => {}

export function configureApiClient(opts: {
  getAccessToken: TokenProvider
  getRefreshToken: TokenProvider
  setTokens: TokenSetter
  onLogout: LogoutHandler
  executeRefresh: RefreshExecutor
  syncTokensFromStorage?: () => void | Promise<void>
}): void {
  getAccessToken = opts.getAccessToken
  getRefreshToken = opts.getRefreshToken
  setTokens = opts.setTokens
  onLogout = opts.onLogout
  executeRefresh = opts.executeRefresh
  syncTokensFromStorage = opts.syncTokensFromStorage ?? (() => {})
}

export const api: AxiosInstance = axios.create({
  baseURL: env.apiUrl,
  withCredentials: false,
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json' },
})

/**
 * Admin impersonation ("vision client"). When set, every user-facing GET
 * carries `user_id` so the API answers as the impersonated subscriber, and
 * writes are blocked client-side: without `user_id` they would silently hit
 * the ADMIN's own account. `/admin/*` (already scoped) and `/auth/*` (token
 * refresh) are exempt.
 */
let impersonatedUserId: string | null = null

export function setImpersonatedUserId(userId: string | null): void {
  impersonatedUserId = userId
}

/** Thrown on writes during impersonation — shaped so `extractApiMessage` reads it. */
export class ImpersonationWriteError extends Error {
  response = {
    data: {
      success: false,
      error: { code: 'IMPERSONATION_READ_ONLY', message: 'Lecture seule en vision client.' },
    },
  }
  constructor() {
    super('Lecture seule en vision client.')
  }
}

function isImpersonationExempt(url: string | undefined): boolean {
  if (!url) return false
  return url.startsWith('/admin') || url.startsWith('/auth')
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  if (impersonatedUserId && !isImpersonationExempt(config.url)) {
    const method = (config.method ?? 'get').toLowerCase()
    if (method !== 'get') throw new ImpersonationWriteError()
    config.params = { ...(config.params as Record<string, unknown>), user_id: impersonatedUserId }
  }
  return config
})

interface RetryConfig extends AxiosRequestConfig {
  _retry?: boolean
}

let refreshInFlight: Promise<string> | null = null

/**
 * Runs the actual refresh under a cross-tab Web Lock, so that with the app open
 * in several tabs only ONE tab calls `/auth/refresh` at a time. Once a tab holds
 * the lock it re-reads the persisted tokens (`syncTokensFromStorage`): if another
 * tab already rotated the pair while we waited, we reuse its fresh access token
 * instead of replaying our now-consumed refresh token — replaying a consumed
 * token trips the backend's reuse detection and revokes the whole token family
 * (→ forced logout). Falls back to a plain call where Web Locks is unavailable.
 */
async function refreshUnderLock(staleAccessToken: string | null): Promise<string> {
  const run = async (): Promise<string> => {
    await syncTokensFromStorage()
    const current = getAccessToken()
    if (current && current !== staleAccessToken) return current
    const refreshToken = getRefreshToken()
    if (!refreshToken) throw new Error('No refresh token')
    const session = await executeRefresh!(refreshToken)
    setTokens(session.accessToken, session.refreshToken)
    return session.accessToken
  }
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request('alerte-immo:auth-refresh', run)
  }
  return run()
}

async function performRefresh(staleAccessToken: string | null): Promise<string> {
  if (!executeRefresh) throw new Error('apiClient: executeRefresh not configured')
  if (!getRefreshToken()) throw new Error('No refresh token')
  if (!refreshInFlight) {
    refreshInFlight = refreshUnderLock(staleAccessToken).finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false
  // Endpoints qui « possèdent » leur 401 : c'est de la logique métier (OTP ou
  // lien magique invalide/expiré), pas une session expirée. On ne tente donc ni
  // refresh ni déconnexion — sinon un mauvais code déconnecterait l'abonné.
  // `/account/deletion` est la confirmation de suppression (le `$` exclut
  // `/account/deletion/request-code`).
  return (
    /\/auth\/(refresh|signup|request-code|verify-code|magic-login)/.test(url) ||
    /\/account\/deletion\/?$/.test(url)
  )
}

/**
 * Routes authentifiées dont le 401 est ambigu : jeton expiré OU code de
 * changement d'email erroné. Le premier 401 suit le cycle normal (refresh +
 * rejeu) ; un second 401 est alors le code, remonté au formulaire sans
 * déconnexion.
 */
function ownsRetried401(url: string | undefined): boolean {
  return url != null && /\/account\/email\/verify/.test(url)
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined
    // Only 401 means "session expired" → refresh/replay/logout. Everything else
    // (402 FICHE_LOCKED, 403 LISTING_NOT_RECEIVED, 404…) is business logic that
    // must propagate untouched — never log the user out on a 403.
    if (!config || error.response?.status !== 401) {
      throw error
    }
    // Never attempt refresh on auth endpoints themselves (prevents recursion).
    if (isAuthEndpoint(config.url) || config._retry) {
      // For protected calls that already retried, force logout.
      if (!isAuthEndpoint(config.url) && !ownsRetried401(config.url)) onLogout()
      throw error
    }
    // No refresh token available — give up immediately.
    if (!getRefreshToken()) {
      onLogout()
      throw error
    }
    config._retry = true
    // The token that actually got the 401 — lets a concurrent tab's refresh be
    // detected (its new token differs) so we don't replay a consumed one.
    const sentAuth = (config.headers as Record<string, string> | undefined)?.Authorization
    const staleAccessToken =
      typeof sentAuth === 'string' ? sentAuth.replace(/^Bearer\s+/i, '') : null
    try {
      const newToken = await performRefresh(staleAccessToken)
      config.headers = config.headers ?? {}
      ;(config.headers as Record<string, string>).Authorization = `Bearer ${newToken}`
      return api(config)
    } catch (refreshError) {
      // Refresh injoignable (réseau, timeout, 5xx) : la session n'est pas
      // prouvée invalide. On la garde et on propage l'erreur du refresh (pas le
      // 401) pour que l'appelant ne conclue pas à une session expirée.
      if (axios.isAxiosError(refreshError) && (refreshError.response?.status ?? 500) >= 500) {
        throw refreshError
      }
      onLogout()
      throw error
    }
  },
)
